import React, { useState, useEffect } from 'react';
import {
  Modal,
  Form,
  Input,
  Select,
  DatePicker,
  TimePicker,
  InputNumber,
  Button,
  Row,
  Col,
  message,
  Space,
  Tag,
  Typography
} from 'antd';
import { 
  CalendarOutlined, 
  ClockCircleOutlined, 
  UserOutlined,
  PhoneOutlined,
  MailOutlined,
  FileTextOutlined,
  AlertOutlined,
  VideoCameraOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { usePatients, useProviders, useCreateAppointment } from '../hooks/useGraphQL';
import { useNavigate } from 'react-router-dom';

const { TextArea } = Input;
const { Option } = Select;
const { Title } = Typography;

interface AppointmentFormProps {
  visible: boolean;
  onCancel: () => void;
  onSuccess: () => void;
}

interface FormData {
  app_user_id: string;
  patient_id?: string;
  provider_id: string;
  date: dayjs.Dayjs;
  time: dayjs.Dayjs;
  duration: number;
  type: string;
  priority: string;
  consultation_type: string;
  chief_complaint?: string;
  symptoms?: string[];
  appointment_notes?: string;
  patient_phone?: string;
  patient_email?: string;
  total_amount?: number;
  payment_method?: string;
  language_preference?: string;
  booking_source: string;
  timezone: string;
}

const AppointmentForm: React.FC<AppointmentFormProps> = ({
  visible,
  onCancel,
  onSuccess
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [createAndCallLoading, setCreateAndCallLoading] = useState(false);
  const navigate = useNavigate();

  // GraphQL hooks
  const [loadPatients, { data: patientsData, loading: patientsLoading }] = usePatients();
  const { data: providersData, loading: providersLoading } = useProviders();
  const [createAppointment, { loading: createLoading }] = useCreateAppointment();

  const patients = patientsData?.allPatients || [];
  const providers = providersData?.providers || [];

  useEffect(() => {
    if (visible) {
      form.resetFields();
      // Set default values
      form.setFieldsValue({
        date: dayjs(),
        time: dayjs(),
        duration: 30,
        type: 'consultation',
        priority: 'normal',
        consultation_type: 'video_call', // Default to video call for Agora integration
        booking_source: 'web',
        timezone: 'Asia/Hong_Kong',
        language_preference: 'zh-HK'
      });
      // Load patients when form becomes visible
      loadPatients();
    }
  }, [visible, form, loadPatients]);

  const handleSubmit = async (values: FormData) => {
    setLoading(true);
    try {
      const appointmentData = {
        app_user_id: values.app_user_id,
        patient_id: values.patient_id,
        provider_id: values.provider_id,
        date: values.date.format('YYYY-MM-DD'),
        time: values.time.format('HH:mm'),
        duration: values.duration,
        type: values.type,
        priority: values.priority,
        consultation_type: values.consultation_type,
        questionnaire: {
          chief_complaint: values.chief_complaint || undefined,
          symptoms: values.symptoms || undefined
        },
        appointment_notes: values.appointment_notes,
        patient_phone: (() => {
          const raw = values.patient_phone || '';
          const digits = raw.replace(/[^0-9+]/g, '');
          if (/^\+\d{1,3}-\d+$/.test(raw)) return raw;
          if (digits.startsWith('+')) {
            const num = digits.replace(/^\+/, '');
            const local = num.slice(-8);
            const cc = num.slice(0, Math.max(0, num.length - 8)) || '852';
            return `+${cc}-${local}`;
          }
          const stripped = digits.replace(/\D/g, '');
          return `+852-${stripped}`;
        })(),
        patient_email: values.patient_email,
        total_amount: values.total_amount,
        payment_method: values.payment_method,
        language_preference: values.language_preference,
        booking_source: values.booking_source,
        timezone: values.timezone,
        status: 'scheduled',
        medical_history_reviewed: false,
        tenant: 'default'
      };

      const result = await createAppointment({
        variables: {
          createAppointmentDto: appointmentData
        }
      });

      if (result.data?.createAppointment) {
        message.success('Appointment created successfully');
        onSuccess();
        onCancel();
      } else {
        throw new Error('Failed to create appointment');
      }
    } catch (error: any) {
      console.error('Error creating appointment:', error);
      message.error(error.message || 'Failed to create appointment');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAndStartCall = async (values: FormData) => {
    setCreateAndCallLoading(true);
    try {
      const appointmentData = {
        app_user_id: values.app_user_id,
        patient_id: values.patient_id,
        provider_id: values.provider_id,
        date: values.date.format('YYYY-MM-DD'),
        time: values.time.format('HH:mm'),
        duration: values.duration,
        type: values.type,
        priority: values.priority,
        consultation_type: 'video_call', // Force video call for Agora integration
        questionnaire: {
          chief_complaint: values.chief_complaint || undefined,
          symptoms: values.symptoms || undefined
        },
        appointment_notes: values.appointment_notes,
        patient_phone: (() => {
          const raw = values.patient_phone || '';
          const digits = raw.replace(/[^0-9+]/g, '');
          if (/^\+\d{1,3}-\d+$/.test(raw)) return raw;
          if (digits.startsWith('+')) {
            const num = digits.replace(/^\+/, '');
            const local = num.slice(-8);
            const cc = num.slice(0, Math.max(0, num.length - 8)) || '852';
            return `+${cc}-${local}`;
          }
          const stripped = digits.replace(/\D/g, '');
          return `+852-${stripped}`;
        })(),
        patient_email: values.patient_email,
        total_amount: values.total_amount,
        payment_method: values.payment_method,
        language_preference: values.language_preference,
        booking_source: values.booking_source,
        timezone: values.timezone,
        status: 'confirmed', // Set to confirmed for immediate call
        medical_history_reviewed: false,
        tenant: 'default'
      };

      const result = await createAppointment({
        variables: {
          createAppointmentDto: appointmentData
        }
      });

      if (result.data?.createAppointment) {
        const appointmentId = result.data.createAppointment.id;
        message.success('Appointment created and call ready to start');
        onSuccess();
        onCancel();
        
        // Navigate to Agora call page
        navigate(`/agora-call/${appointmentId}`);
      } else {
        throw new Error('Failed to create appointment');
      }
    } catch (error: any) {
      console.error('Error creating appointment for call:', error);
      message.error(error.message || 'Failed to create appointment for call');
    } finally {
      setCreateAndCallLoading(false);
    }
  };

  const getProviderDisplayName = (provider: any) => {
    const doctor = provider.doctor;
    const clinic = provider.clinic;
    const clinicName = clinic?.internal_name || clinic?.name_zh_hk || clinic?.name_en || '';
    return `${doctor.first_name_en} ${doctor.last_name_en} - ${clinicName}`;
  };

  const getPatientDisplayName = (patient: any) => {
    return `${patient.first_name_en} ${patient.last_name_en} (${patient.phone_number})`;
  };

  return (
    <Modal
      title={
        <Space>
          <CalendarOutlined />
          <Title level={4} style={{ margin: 0 }}>Create New Appointment</Title>
        </Space>
      }
      open={visible}
      onCancel={onCancel}
      footer={null}
      width={800}
      confirmLoading={loading}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{
          date: dayjs(),
          time: dayjs(),
          duration: 30,
          type: 'consultation',
          priority: 'normal',
          consultation_type: 'video_call',
          booking_source: 'web',
          timezone: 'Asia/Hong_Kong',
          language_preference: 'zh-HK'
        }}
      >
        <Row gutter={[16, 16]}>
          {/* App User Selection */}
          <Col span={12}>
            <Form.Item
              name="app_user_id"
              label={
                <Space>
                  <UserOutlined />
                  Account Owner
                </Space>
              }
              rules={[{ required: true, message: 'Please select an account owner' }]}
            >
              <Select
                placeholder="Select account owner"
                loading={patientsLoading}
                showSearch
                filterOption={(input, option) =>
                  String(option?.children || '')?.toLowerCase().includes(input.toLowerCase())
                }
              >
                {patients.map((patient: any) => (
                  <Option key={patient.id} value={patient.app_user?.id || patient.id}>
                    {getPatientDisplayName(patient)}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>

          {/* Patient Selection */}
          <Col span={12}>
            <Form.Item
              name="patient_id"
              label={
                <Space>
                  <UserOutlined />
                  Patient
                </Space>
              }
              rules={[{ required: true, message: 'Please select a patient' }]}
            >
              <Select
                placeholder="Select patient"
                loading={patientsLoading}
                showSearch
                filterOption={(input, option) =>
                  String(option?.children || '')?.toLowerCase().includes(input.toLowerCase())
                }
              >
                {patients.map((patient: any) => (
                  <Option key={patient.id} value={patient.id}>
                    {getPatientDisplayName(patient)}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>

          {/* Provider Selection */}
          <Col span={12}>
            <Form.Item
              name="provider_id"
              label={
                <Space>
                  <UserOutlined />
                  Doctor & Clinic
                </Space>
              }
              rules={[{ required: true, message: 'Please select a doctor' }]}
            >
              <Select
                placeholder="Select doctor and clinic"
                loading={providersLoading}
                showSearch
                filterOption={(input, option) =>
                  String(option?.children)?.toLowerCase().includes(input.toLowerCase())
                }
              >
                {providers.map((provider: any) => (
                  <Option key={provider.id} value={provider.id}>
                    {getProviderDisplayName(provider)}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>

          {/* Date and Time */}
          <Col span={8}>
            <Form.Item
              name="date"
              label={
                <Space>
                  <CalendarOutlined />
                  Date
                </Space>
              }
              rules={[{ required: true, message: 'Please select a date' }]}
            >
              <DatePicker
                style={{ width: '100%' }}
                format="YYYY-MM-DD"
                disabledDate={(current) => current && current < dayjs().startOf('day')}
              />
            </Form.Item>
          </Col>

          <Col span={8}>
            <Form.Item
              name="time"
              label={
                <Space>
                  <ClockCircleOutlined />
                  Time
                </Space>
              }
              rules={[{ required: true, message: 'Please select a time' }]}
            >
              <TimePicker
                style={{ width: '100%' }}
                format="HH:mm"
                minuteStep={15}
              />
            </Form.Item>
          </Col>

          <Col span={8}>
            <Form.Item
              name="duration"
              label="Duration (minutes)"
              rules={[{ required: true, message: 'Please enter duration' }]}
            >
              <InputNumber
                min={15}
                max={480}
                step={15}
                style={{ width: '100%' }}
              />
            </Form.Item>
          </Col>

          {/* Appointment Type and Priority */}
          <Col span={8}>
            <Form.Item
              name="type"
              label="Appointment Type"
              rules={[{ required: true, message: 'Please select appointment type' }]}
            >
              <Select placeholder="Select type">
                <Option value="consultation">
                  <Tag color="blue">CONSULTATION</Tag>
                </Option>
                <Option value="follow_up">
                  <Tag color="green">FOLLOW UP</Tag>
                </Option>
                <Option value="emergency">
                  <Tag color="red">EMERGENCY</Tag>
                </Option>
                <Option value="telemedicine">
                  <Tag color="purple">TELEMEDICINE</Tag>
                </Option>
                <Option value="vaccination">
                  <Tag color="orange">VACCINATION</Tag>
                </Option>
                <Option value="body_check">
                  <Tag color="gold">BODY CHECK</Tag>
                </Option>
              </Select>
            </Form.Item>
          </Col>

          <Col span={8}>
            <Form.Item
              name="priority"
              label="Priority"
              rules={[{ required: true, message: 'Please select priority' }]}
            >
              <Select placeholder="Select priority">
                <Option value="normal">
                  <Tag color="blue">NORMAL</Tag>
                </Option>
                <Option value="urgent">
                  <Tag color="orange">URGENT</Tag>
                </Option>
                <Option value="emergency">
                  <Tag color="red">EMERGENCY</Tag>
                </Option>
              </Select>
            </Form.Item>
          </Col>

          <Col span={8}>
            <Form.Item
              name="consultation_type"
              label="Consultation Type"
              rules={[{ required: true, message: 'Please select consultation type' }]}
            >
              <Select placeholder="Select consultation type">
                <Option value="in_person">In Person</Option>
                <Option value="video_call">Video Call</Option>
                <Option value="phone_call">Phone Call</Option>
              </Select>
            </Form.Item>
          </Col>

          {/* Contact Information */}
          <Col span={12}>
            <Form.Item
              name="patient_phone"
              label={
                <Space>
                  <PhoneOutlined />
                  Patient Phone
                </Space>
              }
            >
              <Input placeholder="Patient phone number" />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              name="patient_email"
              label={
                <Space>
                  <MailOutlined />
                  Patient Email
                </Space>
              }
            >
              <Input placeholder="Patient email address" />
            </Form.Item>
          </Col>

          {/* Medical Information */}
          <Col span={24}>
            <Form.Item
              name="chief_complaint"
              label={
                <Space>
                  <AlertOutlined />
                  Chief Complaint
                </Space>
              }
            >
              <TextArea
                rows={3}
                placeholder="Describe the main reason for the appointment"
              />
            </Form.Item>
          </Col>

          <Col span={24}>
            <Form.Item
              name="appointment_notes"
              label={
                <Space>
                  <FileTextOutlined />
                  Notes
                </Space>
              }
            >
              <TextArea
                rows={3}
                placeholder="Additional notes or instructions"
              />
            </Form.Item>
          </Col>

          {/* Payment Information */}
          <Col span={8}>
            <Form.Item
              name="total_amount"
              label="Total Amount"
            >
              <InputNumber
                min={0}
                step={0.01}
                style={{ width: '100%' }}
                placeholder="0.00"
                formatter={(value) => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
              />
            </Form.Item>
          </Col>

          <Col span={8}>
            <Form.Item
              name="payment_method"
              label="Payment Method"
            >
              <Select placeholder="Select payment method">
                <Option value="cash">Cash</Option>
                <Option value="credit_card">Credit Card</Option>
                <Option value="online">Online</Option>
                <Option value="insurance">Insurance</Option>
              </Select>
            </Form.Item>
          </Col>

          <Col span={8}>
            <Form.Item
              name="language_preference"
              label="Language Preference"
            >
              <Select placeholder="Select language">
                <Option value="zh-HK">Chinese (Hong Kong)</Option>
                <Option value="en">English</Option>
              </Select>
            </Form.Item>
          </Col>

          {/* Hidden fields */}
          <Col span={24} style={{ display: 'none' }}>
            <Form.Item name="booking_source">
              <Input />
            </Form.Item>
            <Form.Item name="timezone">
              <Input />
            </Form.Item>
          </Col>
        </Row>

        {/* Form Actions */}
        <Row justify="end" style={{ marginTop: 24 }}>
          <Space>
            <Button onClick={onCancel}>
              Cancel
            </Button>
            <Button
              type="primary"
              htmlType="submit"
              loading={loading || createLoading}
            >
              Create Appointment
            </Button>
            <Button
              type="primary"
              icon={<VideoCameraOutlined />}
              loading={createAndCallLoading}
              onClick={() => form.validateFields().then(handleCreateAndStartCall)}
              style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}
            >
              Create & Start Call
            </Button>
          </Space>
        </Row>
      </Form>
    </Modal>
  );
};

export default AppointmentForm; 